import { useState } from 'react'
import { FilterButton, WideFilters } from '@/components/Filters'
import { Segmented } from '@/components/Segmented'
import { Switch } from '@/components/Switch'
import { WhoseBar, WhoseButton } from '@/components/Whose'
import { useWhosePicker } from '@/hooks/useWhosePicker'
import { ChevronLeftIcon, ChevronRightIcon, MoreIcon, SearchIcon, SelectIcon } from '@/components/icons'
import { addDays, startOfWeek } from '@/logic/dates'
import { weekTitle } from '@/logic/labels'
import { canConnectGoogle } from '@/lib/google'
import { useAuth } from '@/store/auth'
import { connectGoogle, useCalendar } from '@/store/calendar'
import { useHousehold } from '@/store/household'
import { usePrefs, type EventsMode, type PlanLayout } from '@/store/prefs'
import { setPlanWeek, setSearchOpen, startSelecting, stopSelecting, useUi } from '@/store/ui'
import type { DateStr } from '@/types'

const EVENT_MODES: { value: EventsMode; label: string }[] = [
  { value: 'count', label: 'Count' },
  { value: 'all', label: 'Show all' },
  { value: 'off', label: 'Off' },
]

const LAYOUTS: { value: PlanLayout; label: string }[] = [
  { value: 'stacked', label: 'Stacked' },
  { value: 'columns', label: 'Columns' },
]

/** The week's name over its dates, "This week" / "12 – 18 Oct"; a past week's small line adds "Read-only". Tapping returns to this week. */
function WeekTitle({ week, today }: { week: DateStr; today: DateStr }) {
  const monday = startOfWeek(today)
  const { name, span } = weekTitle(week, monday)
  const past = week < monday
  return (
    <button type="button" className="ptop__week" onClick={() => setPlanWeek(monday)} title="Back to this week">
      {name || past ? (
        <>
          <b>{name ?? span}</b>
          <small>{past ? (name ? `${span} · Read-only` : 'Read-only') : span}</small>
        </>
      ) : (
        <span className="ptop__only">{span}</span>
      )}
    </button>
  )
}

function Arrows({ week, children }: { week: DateStr; children: React.ReactNode }) {
  return (
    <>
      <button type="button" className="icon-btn" aria-label="Previous week" onClick={() => setPlanWeek(addDays(week, -7))}>
        <ChevronLeftIcon size={18} weight={2.4} />
      </button>
      {children}
      <button type="button" className="icon-btn" aria-label="Next week" onClick={() => setPlanWeek(addDays(week, 7))}>
        <ChevronRightIcon size={18} weight={2.4} />
      </button>
    </>
  )
}

/** The ⋯ menu: Select and the layout (on a phone, where the top row has no room for Select), Hide done, and the Events setting. */
function OptionsMenu({ wide, canSelect = false }: { wide: boolean; canSelect?: boolean }) {
  const [open, setOpen] = useState(false)
  const layout = usePrefs((s) => (wide ? s.layoutWide : s.layoutPhone))
  const setLayout = usePrefs((s) => s.setLayout)
  const events = usePrefs((s) => s.events)
  const setEvents = usePrefs((s) => s.setEvents)
  const hideDone = usePrefs((s) => !!s.filter.hideDone)
  const setFilter = usePrefs((s) => s.setFilter)
  return (
    <span className="ptop__menu-wrap">
      <button type="button" className="icon-btn" aria-label="Options" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <MoreIcon size={20} weight={2.6} />
      </button>
      {open && (
        <>
          <button type="button" className="ptop__menu-scrim" aria-label="Close options" tabIndex={-1} onClick={() => setOpen(false)} />
          <div className="ptop__menu" role="dialog" aria-label="Options" onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}>
            {!wide && canSelect && (
              <button
                type="button"
                className="ptop__menu-row"
                onClick={() => {
                  setOpen(false)
                  startSelecting()
                }}
              >
                <SelectIcon size={18} />
                Select tasks
              </button>
            )}
            {/* a filter like Whose and Category, so it holds on the Calendar tab too */}
            <button
              type="button"
              className="ptop__menu-row ptop__menu-row--switch"
              aria-pressed={hideDone}
              onClick={() => {
                setFilter({ hideDone: !hideDone })
                setOpen(false)
              }}
            >
              Hide done tasks
              <Switch on={hideDone} />
            </button>
            {!wide && (
              <>
                <h3 className="eyebrow">Layout</h3>
                <Segmented
                  label="Layout"
                  fill
                  value={layout}
                  options={LAYOUTS}
                  onChange={(v) => {
                    setLayout(false, v)
                    setOpen(false)
                  }}
                />
              </>
            )}
            <h3 className="eyebrow">Events</h3>
            <Segmented
              label="Events"
              fill
              value={events}
              options={EVENT_MODES}
              onChange={(v) => {
                setEvents(v)
                setOpen(false)
              }}
            />
          </div>
        </>
      )}
    </span>
  )
}

/** Select on a wide screen: several tasks at once. Pressed while selecting, it ends Select mode. Not on a past week, which is read-only. */
function SelectButton({ week, today }: { week: DateStr; today: DateStr }) {
  const selecting = useUi((s) => s.selecting)
  if (week < startOfWeek(today)) return null
  return (
    <button type="button" className={`icon-btn ${selecting ? 'icon-btn--on' : ''}`} aria-label="Select tasks" aria-pressed={selecting} onClick={() => (selecting ? stopSelecting() : startSelecting())}>
      <SelectIcon size={20} />
    </button>
  )
}

/** With no Google Calendar connected, one line in place of the event counts. */
function ConnectRow() {
  const connection = useCalendar((s) => s.connection)
  const busy = useCalendar((s) => s.busy)
  const online = useUi((s) => s.online)
  const email = useAuth((s) => s.user?.email ?? '')
  const settings = useHousehold((s) => s.userDoc?.settings)
  const events = usePrefs((s) => s.events)
  if (events === 'off' || !canConnectGoogle || (connection !== 'disconnected' && connection !== 'revoked')) return null
  return (
    <div className="ptop__connect">
      <span>{connection === 'revoked' ? 'Google Calendar access has lapsed.' : 'See your Google Calendar events on each day.'}</span>
      <button type="button" className="textbtn" disabled={busy || !online} onClick={() => void connectGoogle(email, settings).catch(() => undefined)}>
        {connection === 'revoked' ? 'Reconnect' : 'Connect Google Calendar'}
      </button>
    </div>
  )
}

function OfflineRow() {
  const online = useUi((s) => s.online)
  if (online) return null
  return <div className="ptop__offline">Offline · changes will sync when you're back online</div>
}

/** A phone's one top row: the week and its arrows on the left, search, Whose, filters and options on the right; Whose's bar opens under it. It scrolls away with the page. */
export function PlanTopRow({ week, today }: { week: DateStr; today: DateStr }) {
  const whose = useWhosePicker()
  return (
    <>
      <div className="ptop">
        <Arrows week={week}>
          <WeekTitle week={week} today={today} />
        </Arrows>
        <span className="ptop__spacer" />
        <button type="button" className="icon-btn" aria-label="Search" onClick={() => setSearchOpen(true)}>
          <SearchIcon size={20} />
        </button>
        <WhoseButton picker={whose} />
        <FilterButton />
        <OptionsMenu wide={false} canSelect={week >= startOfWeek(today)} />
      </div>
      <WhoseBar picker={whose} />
      <OfflineRow />
      <ConnectRow />
    </>
  )
}

/** A wide screen's week row under the tabs: the week, the layout switch and the filters stay in view. */
export function PlanWeekRow({ week, today }: { week: DateStr; today: DateStr }) {
  const layout = usePrefs((s) => s.layoutWide)
  const setLayout = usePrefs((s) => s.setLayout)
  return (
    <>
      <div className="ptop ptop--wide">
        <Arrows week={week}>
          <WeekTitle week={week} today={today} />
        </Arrows>
        <span className="ptop__layout">
          <Segmented label="Layout" value={layout} options={[...LAYOUTS].reverse()} onChange={(v) => setLayout(true, v)} />
        </span>
        <span className="ptop__spacer" />
        <WideFilters />
        <SelectButton week={week} today={today} />
        <OptionsMenu wide />
      </div>
      <OfflineRow />
      <ConnectRow />
    </>
  )
}
